import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  Firestore 
} from 'firebase/firestore';
import { getFunctions, httpsCallable, Functions } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import UserIcon from '../authentication/UserIcon';
import app from '../../Firebase';
import '../../styles/AdminPanel.css';
import CollectionManager from './CollectionManager';
import CollectionView from './CollectionView';
import ApiKeyGenerator from './ApiKeyGenerator';
import LoadingPage from '../LoadingPage';

import "../../styles/AdminNavigationStyles.css"

/**
 * Admin Panel - Requires APP_ADMIN permission
 * 
 * Permissions allowed in this component:
 * - APP_ADMIN: Can view all content and access all features
 * 
 * Future permissions to consider:
 * - CONFERENCE_ADMIN: Could be limited to only managing conferences
 * - USER_ADMIN: Could be limited to only managing users
 * - CONTENT_ADMIN: Could be limited to only managing content
 * - APP_MANAGER: Could be limited to only managing apps
 */

const AdminPanel: React.FC<{ db: Firestore }> = ({ db }) => {
  const { currentUser } = useAuth();
  const [activeSection, setActiveSection] = useState<string | null>("collections");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  // const functions = getFunctions(app);
  const functions = getFunctions(app, "europe-west2");
  
  // Clear success and error messages when changing sections
  useEffect(() => {
    setError('');
    setSuccess('');
  }, [activeSection]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  // Helper to automatically dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="header-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img
            style={{ height: "30px" }}
            src="https://i.ibb.co/msQ30NW/Screenshot-2025-06-06-at-23-42-22.png"
            alt="Logo"
          />
          <h1 style={{ margin: 0 }}>Collections Manager</h1>
        </div>
        <div className="header-user">
          <UserIcon />
        </div>
      </header>

      {error && (
        <div className="error-alert">
          <button className="close-alert" onClick={() => setError('')}>×</button>
          {error}
        </div>
      )}

      {success && (
        <div className="success-alert">
          <button className="close-alert" onClick={() => setSuccess('')}>×</button>
          {success}
        </div>
      )}

      <div className="admin-content">
        {/* New Sidebar */}
        <div className="collections-sidebar-nav">
          <div className="collections-sidebar-nav-header">
            <h3 className="collections-sidebar-nav-title">Administration</h3>
            <p className="collections-sidebar-nav-subtitle">Manage your applications</p>
          </div>
          
          <div className="collections-sidebar-nav-navigation">
            <div className="collections-nav-section">
              <div className="collections-nav-section-title">Navigation</div>
              <button 
                type="button" 
                className={`collections-nav-item ${activeSection === 'collections' ? 'collections-nav-item-active' : ''}`}
                onClick={() => handleSectionChange('collections')}
              >
                <span className="collections-nav-icon">📊</span>
                Manage Collections
              </button>
              
              <button 
                type="button" 
                className={`collections-nav-item ${activeSection === 'cid-organizations' ? 'collections-nav-item-active' : ''}`}
                onClick={() => handleSectionChange('cid-organizations')}
              >
                <span className="collections-nav-icon">🏢</span>
                Manage Organizations
              </button>
              
              <button 
                type="button" 
                className={`collections-nav-item ${activeSection === 'cid-apps' ? 'collections-nav-item-active' : ''}`}
                onClick={() => handleSectionChange('cid-apps')}
              >
                <span className="collections-nav-icon">📱</span>
                Manage Apps
              </button>
              
              <button 
                type="button" 
                className={`collections-nav-item ${activeSection === 'apiaccess' ? 'collections-nav-item-active' : ''}`}
                onClick={() => handleSectionChange('apiaccess')}
              >
                <span className="collections-nav-icon">🔑</span>
                API Access
              </button>
            </div>
          </div>
          
          <div className="collections-organization-info">
            <h4>Collections System</h4>
            <p>Current Environment</p>
          </div>
        </div>

        <div className="admin-main-content">
          {!activeSection && (
            <div className="empty-section">
              <h2>Welcome to Admin Panel</h2>
              <p>Select a section from the sidebar to get started.</p>
            </div>
          )}

        {activeSection === 'collections' && (
            <CollectionManager 
                db={db}
                functions={functions}
                setError={setError}
                setSuccess={setSuccess}
                loading={loading}
                setLoading={setLoading}
            />
        )}

        {activeSection?.includes('cid-') && (
            <CollectionView 
                collectionId={activeSection.split("-")[1]} 
                instanceAlias={
                  (() => {
                    const section = activeSection.split("-")[1];
                    const singular = section.endsWith("s") ? section.slice(0, -1) : section;
                    return singular.charAt(0).toUpperCase() + singular.slice(1);
                  })()
                }
                db={db}
                functions={functions}
                setError={setError}
                setSuccess={setSuccess}
                loading={loading}
                setLoading={setLoading}
            />
        )}

        {activeSection === 'apiaccess' && (
            <ApiKeyGenerator
                functions={functions}
                userEmail={currentUser?.email || null}
            />
        )}

        </div>
      </div>

      {loading && (
        <LoadingPage />
      )}
    </div>
  );
};

export default AdminPanel;